import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { capture } from '@/lib/analytics';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Loader2,
} from 'lucide-react';
import { OrbisMarkSVG } from '@/components/OrbisLogo';
import OrbisSpinner from '@/components/ui/orbis-spinner';
import GlassNav from '@/components/magic/GlassNav';
import CreditMeter from '@/components/CreditMeter';

// Import stage components
import BrandFoundations from '@/components/magic/BrandFoundations';
import BriefStage from '@/components/magic/BriefStage';
import DesignSystemStage from '@/components/magic/DesignSystemStage';
import StructureStage from '@/components/magic/StructureStage';
import AssetsStage from '@/components/magic/AssetsStage';
import BuildStage from '@/components/magic/BuildStage';
import ReviewStage from '@/components/magic/ReviewStage';
import ContentStage from '@/components/magic/ContentStage';
import ContentTransition from '@/components/magic/ContentTransition';
import DeployStage from '@/components/magic/DeployStage';
import StructureTransition from '@/components/magic/StructureTransition';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  { id: 'brief', label: 'Brief' },
  { id: 'design_system', label: 'Design' },
  { id: 'structure', label: 'Structure' },
  { id: 'content', label: 'Content' },
  { id: 'assets', label: 'Assets' },
  { id: 'build', label: 'Build' },
  { id: 'review', label: 'Review' },
  { id: 'deploy', label: 'Deploy' },
];

function getApiErrorMessage(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) return detail.map((d) => String(d)).join(' | ');
  if (detail && typeof detail === 'object') {
    if (typeof detail.message === 'string' && detail.message.trim()) return detail.message;
    try {
      return JSON.stringify(detail);
    } catch (_e) {
      return fallback;
    }
  }
  return fallback;
}

function notifyGenerationOutcome(stageLabel, meta) {
  if (!meta || typeof meta !== 'object') return;
  const status = (meta.status || '').toLowerCase();
  const reason = typeof meta.reason === 'string' && meta.reason.trim() ? meta.reason.trim() : '';
  if (status === 'fallback') {
    toast.warning(
      reason
        ? `${stageLabel} generated using fallback draft. Reason: ${reason}`
        : `${stageLabel} generated using fallback draft.`,
    );
    return;
  }
  if (status === 'mixed') {
    toast.warning(
      reason
        ? `${stageLabel} generated with mixed AI/fallback output. Reason: ${reason}`
        : `${stageLabel} generated with mixed AI/fallback output.`,
    );
  }
}

/* Old Stepper removed — replaced by GlassNav component */

export default function MagicModePage() {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [magicState, setMagicState] = useState({
    current_stage: 'brief',
    completed_stages: [],
    brief: null,
    design_system: null,
    structure: null,
    image_slots: [],
    generated_code: null
  });
  const [viewStage, setViewStage] = useState('brief');
  const [designDiff, setDesignDiff] = useState(null);
  const [isGeneratingStructure, setIsGeneratingStructure] = useState(false);
  const [isGeneratingContent, setIsGeneratingContent] = useState(false);
  const [fallbackStages, setFallbackStages] = useState({});  // { structure: reason, content: reason }
  const prefersReducedMotion = useReducedMotion();

  // Preserve brand_context when updating magicState from API responses.
  // options.preserveView = true → do NOT sync viewStage (used for in-place edits
  // like section insert, element edits, animations — anything that shouldn't navigate).
  const updateMagicState = (newState, options = {}) => {
    if (!newState) return;
    const incoming = typeof newState === 'function' ? newState(magicState) : newState;
    const merged = (magicState.brand_context && !incoming.brand_context)
      ? { ...incoming, brand_context: magicState.brand_context }
      : incoming;
    setMagicState(merged);
    if (!options.preserveView && merged.current_stage) {
      setViewStage(merged.current_stage);
    }
  };

  useEffect(() => {
    fetchProject();
  }, [projectId]);

  useEffect(() => {
    setViewStage(magicState.current_stage);
  }, [magicState.current_stage]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${API_URL}/magic/projects/${projectId}`);
      if (response.data.magic_mode === false) {
        navigate(`/workspace/${projectId}`, { replace: true });
        return;
      }
      setProject(response.data);
      if (response.data.magic_state) {
        updateMagicState(response.data.magic_state);
      }
    } catch (error) {
      toast.error('Failed to load project');
      navigate('/dashboard');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStageClick = (stageId) => {
    if (isProcessing) return;
    const stageIdx = STAGES.findIndex(s => s.id === stageId);
    const currentIdx = STAGES.findIndex(s => s.id === magicState.current_stage);
    if (stageIdx <= currentIdx || (magicState.completed_stages || []).includes(stageId)) {
      setViewStage(stageId);
    }
  };

  const handleBrandComplete = async (brandContext) => {
    const updatedState = { ...magicState, brand_context: brandContext };

    // If brand has colors/fonts, pre-lock them in design_system_locked
    if (brandContext.brand_path !== 'none') {
      updatedState.design_system_locked = {
        palette: !!(brandContext.colors?.primary),
        typography: !!(brandContext.heading_font || brandContext.body_font),
        style: false,
      };
    }

    // Persist brand_context to backend so it survives page refreshes
    try {
      await axios.post(`${API_URL}/magic/projects/${projectId}/brand-context`, brandContext);
    } catch (e) {
      // Non-fatal — brand context is also preserved in local state
    }

    setMagicState(updatedState);
  };

  const handleApproveAndContinue = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      const response = await axios.post(`${API_URL}/magic/projects/${projectId}/approve`, {
        project_id: projectId,
        stage: magicState.current_stage,
        approved: true
      });
      capture('magic_stage_approved', { project_id: projectId, stage: magicState.current_stage });
      updateMagicState(response.data.magic_state);
      toast.success('Stage approved!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve stage');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleGenerateDesignSystem = async (opts = {}) => {
    setIsProcessing(true);
    try {
      const response = await axios.post(`${API_URL}/magic/projects/${projectId}/generate-design-system`, {
        locked: opts?.locked || {},
        overrides: opts?.overrides || {},
        refinement_text: opts?.refinement_text || null,
        selected_style: opts?.selected_style || null,
      });
      updateMagicState(response.data.magic_state);
      setDesignDiff(response.data.diff || null);
      toast.success(opts?.refinement_text ? 'Design system refined!' : 'Design system generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate design system');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleGenerateStructure = async (overrides = {}) => {
    if (isProcessing) return;
    setIsProcessing(true);
    setIsGeneratingStructure(true);
    try {
      // Save any manual design system overrides (colors, fonts) before generating structure
      if (overrides.colors || overrides.headingFont || overrides.bodyFont) {
        await axios.post(`${API_URL}/magic/projects/${projectId}/update-design-system`, {
          colors: overrides.colors,
          heading_font: overrides.headingFont,
          body_font: overrides.bodyFont
        });
      }
      const response = await axios.post(`${API_URL}/magic/projects/${projectId}/generate-structure`);
      updateMagicState(response.data.magic_state);
      const meta = response.data.generation_meta;
      const isFb = (meta?.status || '').toLowerCase() === 'fallback';
      const isMixed = (meta?.status || '').toLowerCase() === 'mixed';
      if (isFb || isMixed) {
        setFallbackStages(prev => ({ ...prev, structure: meta.reason || 'AI generation failed' }));
      } else {
        setFallbackStages(prev => { const n = { ...prev }; delete n.structure; return n; });
        toast.success('Structure generated!');
      }
      notifyGenerationOutcome('Structure', meta);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to generate structure'));
    } finally {
      setIsProcessing(false);
      setIsGeneratingStructure(false);
    }
  };

  const handleGenerateContent = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    setIsGeneratingContent(true);
    try {
      // generate-content endpoint handles all state management
      // (sets current_stage="content", adds "structure" to completed_stages)
      const response = await axios.post(`${API_URL}/magic/projects/${projectId}/generate-content`);
      updateMagicState(response.data.magic_state);
      const meta = response.data.generation_meta;
      const isFb = (meta?.status || '').toLowerCase() === 'fallback';
      const isMixed = (meta?.status || '').toLowerCase() === 'mixed';
      if (isFb || isMixed) {
        setFallbackStages(prev => ({ ...prev, content: meta.reason || 'AI generation failed' }));
      } else {
        setFallbackStages(prev => { const n = { ...prev }; delete n.content; return n; });
        toast.success('Content & art direction generated!');
      }
      notifyGenerationOutcome('Content', meta);
    } catch (error) {
      console.error('Content generation error:', error.response?.data || error.message);
      toast.error(getApiErrorMessage(error, 'Failed to generate content'));
    } finally {
      setIsProcessing(false);
      setIsGeneratingContent(false);
    }
  };

  const handleGenerateAssets = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      // generate-assets endpoint handles state management
      // (sets current_stage="assets", adds "content" to completed_stages)
      const response = await axios.post(`${API_URL}/magic/projects/${projectId}/generate-assets`);
      updateMagicState(response.data.magic_state);
      toast.success('Assets prepared!');
    } catch (error) {
      console.error('Assets generation error:', error.response?.data || error.message || error);
      toast.error(error.response?.data?.detail || 'Failed to generate assets');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBuild = async () => {
    setViewStage('build');
  };

  const handleUpdateBrief = async (brief) => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      const response = await axios.post(`${API_URL}/magic/projects/${projectId}/brief`, brief);
      updateMagicState(response.data.magic_state);
      toast.success('Brief updated!');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update brief'));
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeploy = async () => {
    if (isProcessing) return;
    if (!project?.custom_domain) {
      toast.error('Please set a custom domain first');
      return;
    }
    setIsProcessing(true);
    try {
      const response = await axios.post(`${API_URL}/deploy`, { project_id: projectId });
      capture('site_published', { project_id: projectId, mode: 'magic', url: response.data.url });
      toast.success(response.data.message);
      if (response.data.url) {
        toast.info(`Live at: ${response.data.url}`);
      }
      await fetchProject();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Deployment failed');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await axios.get(`${API_URL}/projects/${projectId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${project?.name || 'website'}_code.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Code downloaded!');
    } catch (error) {
      toast.error('Failed to download');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--orbis-bg)' }}>
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <OrbisSpinner size={64} color="var(--orbis-blue)" />
          </div>
          <p style={{ color: 'rgba(240,239,232,0.28)', fontWeight: 300 }}>Loading your project...</p>
        </div>
      </div>
    );
  }

  const renderStage = () => {
    switch (viewStage) {
      case 'brief':
        return (
          <BriefStage
            brief={magicState.brief}
            brandContext={magicState.brand_context}
            onUpdate={handleUpdateBrief}
            onContinue={handleGenerateDesignSystem}
            isProcessing={isProcessing}
          />
        );
      case 'design_system':
        if (isGeneratingStructure) {
          return (
            <StructureTransition
              designSystem={magicState.design_system}
              brief={magicState.brief}
              isComplete={!isProcessing && !isGeneratingStructure}
              onContinue={() => setViewStage('structure')}
            />
          );
        }
        return (
          <DesignSystemStage
            designSystem={magicState.design_system}
            magicState={magicState}
            onRegenerate={handleGenerateDesignSystem}
            onApprove={handleGenerateStructure}
            isProcessing={isProcessing}
            designDiff={designDiff}
          />
        );
      case 'structure':
        if (isGeneratingContent) {
          return (
            <ContentTransition
              brief={magicState.brief}
              structure={magicState.structure}
              designSystem={magicState.design_system}
              isComplete={!isProcessing && !isGeneratingContent}
              onContinue={() => setViewStage('content')}
            />
          );
        }
        return (
          <StructureStage
            structure={magicState.structure}
            brief={magicState.brief}
            brandContext={magicState.brand_context}
            onRegenerate={handleGenerateStructure}
            onApprove={handleGenerateContent}
            isProcessing={isProcessing}
            projectId={projectId}
            fallbackReason={fallbackStages.structure || null}
            onAcceptFallback={() => setFallbackStages(prev => { const n = { ...prev }; delete n.structure; return n; })}
          />
        );
      case 'content':
        if (isGeneratingContent) {
          return (
            <ContentTransition
              brief={magicState.brief}
              structure={magicState.structure}
              designSystem={magicState.design_system}
              isComplete={!isProcessing && !isGeneratingContent}
              onContinue={() => setViewStage('content')}
            />
          );
        }
        return (
          <ContentStage
            content={magicState.content}
            structure={magicState.structure}
            brief={magicState.brief}
            designSystem={magicState.design_system}
            onGenerate={handleGenerateContent}
            onApprove={handleGenerateAssets}
            isProcessing={isProcessing}
            projectId={projectId}
            fallbackReason={fallbackStages.content || null}
            onAcceptFallback={() => setFallbackStages(prev => { const n = { ...prev }; delete n.content; return n; })}
          />
        );
      case 'assets':
        return (
          <AssetsStage
            projectId={projectId}
            imageSlots={magicState.image_slots}
            onContinue={handleBuild}
            isProcessing={isProcessing}
            onSlotUpdate={(slotId, url, extraFields) => {
              setMagicState(prev => ({
                ...prev,
                image_slots: prev.image_slots.map(s =>
                  s.id === slotId ? { ...s, placeholder_url: url, ...(extraFields || {}) } : s
                )
              }));
            }}
          />
        );
      case 'build':
        return (
          <BuildStage
            projectId={projectId}
            alreadyCompleted={(magicState.completed_stages || []).includes('build')}
            existingCode={magicState.generated_code}
            existingPages={magicState.generated_pages}
            generationMode={magicState.generation_mode}
            structure={magicState.structure}
            onBuildComplete={(newMagicState) => {
              updateMagicState(newMagicState);
              notifyGenerationOutcome('Build', newMagicState?.generation_meta?.build);
            }}
            onComplete={() => setViewStage('review')}
          />
        );
      case 'review':
        return (
          <ReviewStage
            code={magicState.generated_code}
            designSystem={magicState.design_system}
            generationMode={magicState.generation_mode}
            generatedPages={magicState.generated_pages}
            projectId={projectId}
            onApprove={handleApproveAndContinue}
            onDownload={handleDownload}
            onEdit={(newMagicState) => updateMagicState(newMagicState, { preserveView: true })}
            isProcessing={isProcessing}
            mode="magic"
            modeContext={{
              projectName: project?.name,
              generationMode: magicState.generation_mode,
              reviewEditor: magicState.review_editor || {},
              variableFontAxes: magicState.design_system?.variable_font_axes || {},
            }}
            reviewCapabilities={{
              supportsReferenceContext: false,
            }}
          />
        );
      case 'deploy':
        return (
          <DeployStage
            project={project}
            onDeploy={handleDeploy}
            onDownload={handleDownload}
            isProcessing={isProcessing}
            onEditSite={() => setViewStage('review')}
          />
        );
      default:
        return null;
    }
  };

  const stageTransition = prefersReducedMotion
    ? { duration: 0 }
    : { duration: 0.2, ease: [0.22, 1, 0.36, 1] };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'var(--surface-0)' }}>
      {/* ── Top Nav Bar — Glass nav centered ── */}
      <header className="sticky top-0 z-50 flex items-center justify-center" style={{ height: '64px', padding: '0 20px', background: 'var(--surface-0)' }}>
        {/* Left: breadcrumb */}
        <div className="absolute top-1/2 -translate-y-1/2 flex items-center gap-2.5" style={{ left: 20 }}>
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-1.5 hover:opacity-80 transition-opacity cursor-pointer group"
            style={{ background: 'none', border: 'none', padding: 0 }}
            title="Back to Projects"
          >
            <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-0.5 transition-transform" style={{ color: 'var(--orbis-t3)' }} />
            <OrbisMarkSVG size={20} />
            <span style={{ fontSize: '14px', fontWeight: 400, color: 'var(--orbis-t2)', fontFamily: 'var(--orbis-font)' }}>Projects</span>
          </button>
          <span style={{ fontSize: '14px', color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-font)' }}>/</span>
          <span style={{ fontSize: '14px', fontWeight: 500, color: 'var(--orbis-t1)', fontFamily: 'var(--orbis-font)' }}>{project?.name || 'Project'}</span>
        </div>

        {/* Center: Glass nav */}
        <GlassNav
          viewStage={viewStage}
          currentStage={magicState.current_stage}
          completedStages={magicState.completed_stages || []}
          onStageClick={handleStageClick}
          disabled={isProcessing}
        />

        {/* Right: credits + step count + badge */}
        <div className="absolute top-1/2 -translate-y-1/2 flex items-center gap-3" style={{ right: 20 }}>
          <CreditMeter compact />
          <span style={{
            fontSize: '11px', fontWeight: 400, color: 'var(--orbis-t4)',
            fontFamily: 'var(--orbis-font)', letterSpacing: '0.5px',
          }}>
            Step {STAGES.findIndex(s => s.id === viewStage) + 1} of {STAGES.length}
          </span>
          <span style={{
            fontSize: '9px', fontFamily: 'var(--orbis-mono)', fontWeight: 500,
            textTransform: 'uppercase', letterSpacing: '1px',
            padding: '4px 10px', borderRadius: '100px',
            background: 'var(--orbis-s2)', border: '0.5px solid var(--orbis-s3)', color: 'var(--orbis-t2)',
          }}>
            Magic Mode
          </span>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {!magicState.brand_context ? (
          <BrandFoundations
            onComplete={handleBrandComplete}
            existingBrandContext={magicState.brand_context}
          />
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={viewStage + (isGeneratingStructure ? '-struct-transition' : '') + (isGeneratingContent ? '-content-transition' : '')}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={stageTransition}
              className="h-full"
            >
              {renderStage()}
            </motion.div>
          </AnimatePresence>
        )}
      </main>
    </div>
  );
}
